import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, RefreshCw, MessageCircle, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatPhoneLocal } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const ADMIN_WA_NUMBER = process.env.REACT_APP_ADMIN_WA || "6281703200075";

export default function Activate() {
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [checking, setChecking] = useState(false);
  const navigating = useRef(false);

  const goToWorkspace = useCallback((data) => {
    if (navigating.current) return;
    navigating.current = true;
    if (data.is_admin) {
      navigate("/admin/pending", { replace: true });
    } else {
      navigate(data.wedding?.setup_complete ? "/dashboard" : "/setup", { replace: true });
    }
  }, [navigate]);

  // Cek status; hanya pindah halaman jika akun sudah disetujui.
  const checkStatus = async () => {
    setChecking(true);
    try {
      const data = await refresh(); // tidak pernah throw; null = gagal cek
      if (!data?.user) {
        toast.error("Sesi berakhir. Silakan login kembali.");
        navigate("/login", { replace: true });
      } else if (data.is_admin) {
        navigate("/admin/pending", { replace: true });
      } else if (data.membership) {
        goToWorkspace(data);
      } else {
        toast.info("Akun Anda masih menunggu persetujuan admin.");
      }
    } finally {
      setChecking(false);
    }
  };

  // Halaman otomatis membuka dashboard begitu admin menyetujui akun.
  useEffect(() => {
    const id = setInterval(async () => {
      const data = await refresh();
      if (data?.user && data.membership) goToWorkspace(data);
    }, 15000);
    return () => clearInterval(id);
  }, [refresh, goToWorkspace]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/login");
    }
  };

  const contactAdminViaWA = () => {
    const phone = formatPhoneLocal(user?.phone);
    const message = `Halo Wedd, saya ingin Memverifikasi akun Weddly Wedding Planner. Akun saya: ${phone}`;
    window.open(`https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="max-w-sm w-full text-center">
        <img
          src="/weddly-logo.png"
          alt="Logo Weddly"
          className="h-16 w-auto object-contain mx-auto mb-4"
        />
        <h1 className="font-serif text-3xl mb-1">Weddly</h1>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6">
          Wedding Planner
        </p>
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-700 px-3 py-1 text-xs font-medium mb-4">
          <Clock className="h-3.5 w-3.5" /> Menunggu persetujuan
        </div>
        <h2 className="font-serif text-2xl mb-2">Akun Menunggu Aktivasi</h2>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          Akun dengan nomor HP{" "}
          <span className="font-medium text-foreground">{formatPhoneLocal(user?.phone)}</span>{" "}
          sudah kami terima dan sedang menunggu persetujuan admin. Selesaikan Pembayaran Melalui
          Whatsapp. Halaman ini akan otomatis membuka dashboard begitu akun Anda telah terverifikasi.
        </p>
        <div className="space-y-3">
          <Button onClick={contactAdminViaWA} className="w-full h-12 rounded-full">
            <MessageCircle className="mr-2 h-4 w-4" /> Verifikasi Admin via WhatsApp
          </Button>
          <Button onClick={checkStatus} disabled={checking} variant="outline" className="w-full h-12 rounded-full">
            <RefreshCw className={`mr-2 h-4 w-4 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Memeriksa..." : "Sudah diaktifkan? Muat ulang"}
          </Button>
        </div>
      </div>
      <footer className="fixed bottom-0 left-0 right-0 pb-6 text-center">
        <Button onClick={logout} variant="ghost" size="sm" className="rounded-full text-muted-foreground">
          <LogOut className="mr-2 h-4 w-4" /> Keluar
        </Button>
      </footer>
    </div>
  );
}
